import { logger } from "./log.js";
import { requestDeadline } from './http.js';
const log = logger("Cache");
export class AssetCache {
    constructor(warn = () => {}, { downloadIdleMs = 30000 } = {}) {
        this.downloadIdleMs = downloadIdleMs;
        this.warn = warn;
        this.pending = new Map();
        this.validated = new Set();
        this.active = new Set();
        this.running = 0;
        this.queue = [];
    }
    async database() {
        if (!globalThis.indexedDB) return null;
        this.db ??= new Promise((resolve, reject) => {
            log.debug("open IndexedDB");
            const request = indexedDB.open("saxtonhell-live-assets", 1);
            request.onupgradeneeded = () => {
                const s = request.result.createObjectStore("assets", {
                    keyPath: "key",
                });
                s.createIndex("accessed", "accessed");
            };
            request.onsuccess = () => {
                log.info("IndexedDB ready");
                resolve(request.result);
            };
            request.onerror = () => reject(request.error);
        }).catch((e) => {
            log.error("IndexedDB unavailable", e);
            this.warn(`Cache unavailable: ${e.message}`);
            return null;
        });
        return this.db;
    }
    async transaction(mode, action) {
        const db = await this.database();
        if (!db) return undefined;
        return new Promise((resolve, reject) => {
            const tx = db.transaction("assets", mode);
            const request = action(tx.objectStore("assets"));
            let result;
            if (request)
                request.onsuccess = () => {
                    result = request.result;
                };
            tx.oncomplete = () => resolve(result);
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error);
        });
    }
    async read(key) {
        try {
            return await this.transaction("readonly", (store) =>
                store.get(key),
            );
        } catch (error) {
            log.error("cache read failed", error, { key });
            this.warn("Cached asset unavailable; downloading again.");
            return undefined;
        }
    }
    async write(record) {
        try {
            await this.transaction("readwrite", (store) => store.put(record));
        } catch (e) {
            log.error("cache write failed", e, { key: record.key, bytes: record.size });
            if (e.name === "QuotaExceededError") {
                try {
                    await this.evict(record.bytes.byteLength * 2);
                } catch {
                    /* Storage may have become unavailable. */
                }
                try {
                    await this.transaction("readwrite", (store) =>
                        store.put(record),
                    );
                    return;
                } catch {
                    /* Keep rendering when storage is exhausted. */
                }
            }
            this.warn(
                "Storage is full or unavailable. New assets are available for this session only.",
            );
        }
    }
    async evict(required) {
        log.info("evict inactive assets", { required });
        const db = await this.database();
        if (!db) return;
        await new Promise((resolve, reject) => {
            const tx = db.transaction("assets", "readwrite"),
                cursor = tx
                    .objectStore("assets")
                    .index("accessed")
                    .openCursor();
            let freed = 0;
            cursor.onsuccess = () => {
                const c = cursor.result;
                if (!c || freed >= required) return;
                if (!this.active.has(c.value.key)) {
                    freed += c.value.size;
                    c.delete();
                }
                c.continue();
            };
            tx.oncomplete = resolve;
            tx.onerror = () => reject(tx.error);
        });
    }
    async usage() {
        const db = await this.database();
        if (!db) return 0;
        return new Promise((resolve, reject) => {
            let size = 0;
            const tx = db.transaction("assets"),
                request = tx.objectStore("assets").openCursor();
            request.onsuccess = () => {
                const c = request.result;
                if (c) {
                    size += c.value.size;
                    c.continue();
                }
            };
            tx.oncomplete = () => resolve(size);
            tx.onerror = () => reject(tx.error);
        });
    }
    async clear() {
        log.info("clear downloaded assets");
        await this.transaction("readwrite", (store) => store.clear());
        this.validated.clear();
    }
    async limited(action) {
        if (this.running >= 8)
            await new Promise((resolve) => this.queue.push(resolve));
        this.running++;
        try {
            return await action();
        } finally {
            this.running--;
            this.queue.shift()?.();
        }
    }
    async fetch(url, signal, progress = () => {}) {
        const path = url.split("?")[0], started = performance.now();
        log.debug("fetch asset", { path });
        this.active.add(url);
        const previous = this.pending.get(url);
        if (previous && !previous.signal?.aborted) {
            log.debug("reuse pending download", { path });
            const bytes = await previous.promise;
            signal?.throwIfAborted();
            return bytes.slice();
        }
        const pending = this.limited(async () => {
            signal?.throwIfAborted();
            const stored = await this.read(url),
                headers = {};
            if (stored && this.validated.has(url)) {
                log.debug("validated cache hit", { path, bytes: stored.size });
                return stored.bytes;
            }
            if (stored?.etag) headers["If-None-Match"] = stored.etag;
            const deadline = requestDeadline(signal, this.downloadIdleMs);
            let reader;
            try {
                const response = await deadline.wait(fetch(url, {
                    signal: deadline.signal,
                    headers,
                    cache: "no-cache",
                    credentials: "same-origin",
                }));
                deadline.reset();
                log.debug("HTTP response", { path, status: response.status, cached: !!stored });
                if (response.status === 409)
                    throw new Error("asset_revision_changed");
                if (response.status === 304 && stored) {
                    stored.accessed = Date.now();
                    await this.write(stored);
                    this.validated.add(url);
                    return stored.bytes;
                }
                if (response.status !== 200)
                    throw new Error(
                        `Asset ${response.status}: ${url.split("?")[0]}`,
                    );
                const total = Number(response.headers.get("Content-Length")),
                    chunks = [];
                reader = response.body.getReader();
                let size = 0;
                while (true) {
                    const { value, done } = await deadline.wait(reader.read());
                    if (done) break;
                    deadline.reset();
                    chunks.push(value);
                    size += value.length;
                    if (size > 512 * 1024 * 1024) {
                        await reader.cancel();
                        throw new Error("Asset exceeds 512 MiB budget");
                    }
                    progress(size, total);
                }
                if (total && size !== total)
                    throw new Error("Incomplete asset response");
                const bytes = new Uint8Array(size);
                let offset = 0;
                for (const chunk of chunks) {
                    bytes.set(chunk, offset);
                    offset += chunk.length;
                }
                signal?.throwIfAborted();
                await this.write({
                    key: url,
                    bytes,
                    size,
                    accessed: Date.now(),
                    etag: response.headers.get("ETag"),
                    revision: response.headers.get("X-Asset-Revision"),
                });
                this.validated.add(url);
                log.debug("download complete", { path, bytes: size, durationMs: performance.now() - started });
                return bytes;
            } finally {
                deadline.dispose();
                void reader?.cancel().catch(() => {});
            }
        });
        const ownership = { promise: pending, signal };
        this.pending.set(url, ownership);
        try {
            return (await pending).slice();
        } catch (error) {
            log.error("asset fetch failed", error, { path });
            throw error;
        } finally {
            if (this.pending.get(url) === ownership) this.pending.delete(url);
        }
    }
}
